import { Box, Link, Stack, Typography } from "@mui/material";
import { DataGrid } from "components/DataGrid";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { IssueCount } from "components/IssueCount";
import { prettyPrintQuantity } from "encamp-shared/src/utils/prettyPrintUnits";
import { ReportingFacilityChemicalQuery } from "generated-graphql/graphql";
import { useMeasurements } from "hooks/useMeasurements";
import { useProducts } from "hooks/useProducts";
import { Link as RouterLink } from "react-router-dom";
import { prettyPrintMeasurementRange } from "util/dates";

import { NoRowsOverlay } from "../Inventory/Facility/NoRowsOverlay";

export const MeasurementsTable = ({
  rfc,
}: {
  rfc: ReportingFacilityChemicalQuery["reportingFacilityChemical"];
}) => {
  const { hasProducts } = useProducts();
  const { hasMeasurements } = useMeasurements(rfc.facilityId);

  if (!hasMeasurements) return null;

  return (
    <Stack spacing={1}>
      <Typography variant="h6">
        Daily Measurements{hasProducts ? " for Other Chemicals" : ""}
      </Typography>
      <DataGrid
        pagination
        pageSizeOptions={[5]}
        disableRowSelectionOnClick
        initialState={{
          pagination: { paginationModel: { pageSize: 5 } },
          sorting: { sortModel: [{ field: "measuredAtUtc", sort: "desc" }] },
        }}
        slots={{
          noRowsOverlay: () => <NoRowsOverlay message="No measurements found." />,
        }}
        rows={rfc.origin?.facilityChemicalMeasurements ?? []}
        columns={[
          {
            field: "measuredAtUtc",
            headerName: "Date",
            flex: 1,
            renderCell(params) {
              return prettyPrintMeasurementRange(
                params.row.measuredAtUtc,
                params.row.endDate ?? undefined
              );
            },
          },
          {
            field: "amount",
            headerName: "Amount",
            flex: 1,
            renderCell(params) {
              return prettyPrintQuantity({
                amount: params.row.amount,
                unit: params.row.unit,
                state: rfc.facility?.state,
              });
            },
          },
          {
            field: "maxAmountLargestContainer",
            headerName: "Container Size",
            flex: 1,
            renderCell(params) {
              if (params.row.maxAmountLargestContainer) {
                // Show the specified container size, this same pattern is used in FacilityChemicalMeasurementDetailsTable
                return (
                  <Box title="Specified container size">
                    {prettyPrintQuantity({
                      amount: params.row.maxAmountLargestContainer,
                      unit: params.row.unit,
                      state: rfc.facility?.state,
                    })}
                  </Box>
                );
              } else {
                // Show the total amount (what would be auto-calculated)
                return (
                  <Box sx={{ fontStyle: "italic", opacity: 0.8 }}>
                    {prettyPrintQuantity({
                      amount: params.row.amount,
                      unit: params.row.unit,
                      state: rfc.facility?.state,
                    })}{" "}
                    <InfoIconTooltip
                      tooltip={
                        <>
                          Auto-calculated from total amount at this location. See{" "}
                          <RouterLink
                            to="https://support.encamp.com/en/articles/8121537"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            here
                          </RouterLink>{" "}
                          for more information.
                        </>
                      }
                    />
                  </Box>
                );
              }
            },
          },
          {
            field: "location",
            headerName: "Location",
            flex: 1,
            valueGetter(_, row) {
              const isOregonFacility = rfc.facility?.state === "OR";
              return isOregonFacility
                ? row.storageLocation?.OR_storageBuilding
                : row.storageLocation?.description;
            },
          },
          {
            field: "storageType",
            headerName: "Storage Type",
            flex: 1,
            valueGetter(_, row) {
              return row.storageType;
            },
          },
          {
            field: "issues",
            headerName: "Issues",
            flex: 1,
            renderCell({ row }) {
              const issues = rfc.issues?.filter(
                (issue) =>
                  issue.modelName === "FacilityChemicalMeasurement" && issue.modelId === row.id
              );
              return <IssueCount issueCount={issues?.length} />;
            },
          },
        ]}
      />
    </Stack>
  );
};
